import type { SupabaseClient } from '../../lib/supabase';
import type { PoDocument, PoSnapshot } from '../cgOrders/PoDocumentView';
import type { StageEvent } from '../cgOrders/stageClock';

/**
 * One job order, as the factory is allowed to see it.
 *
 * The shape mirrors supplier_job_order() field for field. Deliberately NOT
 * derived from the cg_orders row type: that type gains every column the table
 * gains, and a supplier payload built from it would silently widen the day
 * somebody adds a price column. This interface has to be edited by hand for
 * anything new to appear, which is the point.
 *
 * There is a matching guard in the migration harness that reads the function's
 * source and fails if a money column is named in it. Between the two, a leak
 * needs somebody to defeat a database assertion AND write a field here.
 */

export interface JobOrderItem {
  units: number;
  name: string;
  sku: string;
  spec: string;
}

export interface JobOrderProof {
  component: string;
  version: number;
  state: string;
  url: string;
}

export interface JobOrderSampleOption {
  option_id: string;
  component: string;
  round: number;
  /**
   * 'pending' | 'approved' | 'denied' at the database, which enforces it with
   * a CHECK. Typed as string here because this arrives as jsonb: narrowing it
   * would be the browser asserting a shape it did not check, and a fourth
   * verdict added in a migration would land as a lie rather than as a value
   * the UI simply does not recognise.
   */
  verdict: string;
  photo_url: string;
  feedback: string;
}

export interface JobOrderSample {
  submission_id: string;
  title: string;
  status: string;
  created_at: string;
  options: JobOrderSampleOption[];
}

export interface JobOrderReferenceFile {
  kind: string;
  /** What the file is, typed by whoever uploaded it. Empty for the logo. */
  label?: string;
  version: number;
  url: string;
}

export interface JobOrder {
  order_id: string;
  order_no: string;
  status: string;
  ship_to: string;
  target_ship_date: string | null;
  date_po_sent: string | null;
  supplier_confirmed_at: string | null;
  supplier_revision_note: string;
  sample_due_days: number;
  sample_clock_started_at: string | null;
  /** The second promise: ready for collection, once the samples are approved. */
  production_due_days: number;
  production_clock_started_at: string | null;
  supplier_ready_for_shipment: boolean;
  tracking_number: string | null;
  courier_name: string;
  /** Where to follow the shipment. '' when nobody has pasted one. */
  tracking_url: string;
  items: JobOrderItem[];
  proofs: JobOrderProof[];
  /**
   * Files the factory needs that the client never approved — the client's
   * logo. Shown with the proofs, never counted as one. Optional because a job
   * loaded from a function older than 20270105000100 will not carry the key.
   */
  reference_files?: JobOrderReferenceFile[];
  changes_requested: string[];
  samples: JobOrderSample[];
  sample_photo_count: number;
}

export async function fetchJobOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<JobOrder | null> {
  const { data, error } = await client.rpc('supplier_job_order', { p_order: orderId });
  if (error) {
    throw new Error(error.message);
  }
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  return data as unknown as JobOrder;
}

/**
 * The POs issued for this job, as the factory may see them.
 *
 * Its own RPC, not the CG one. Two audiences, two functions — a single
 * function with a role branch inside it is where a field added for TAP reaches
 * a factory by default.
 */
export async function fetchSupplierPoDocuments(
  client: SupabaseClient,
  orderId: string,
): Promise<PoDocument[]> {
  const { data, error } = await client.rpc('supplier_po_documents', { p_order: orderId });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    revision: row.revision,
    label: row.label,
    issued_at: row.issued_at,
    superseded_at: row.superseded_at,
    reason: row.reason,
    snapshot: (typeof row.snapshot === 'object' && row.snapshot !== null
      ? row.snapshot
      : {}) as PoSnapshot,
  }));
}

/**
 * Every stage this job has passed through, with the day counts.
 *
 * Its own RPC on the supplier side, like the PO and the certificate. The
 * factory seeing how long each of its own stages took is the point — a
 * measurement nobody can see creates no pressure at all.
 */
export async function fetchSupplierStageEvents(
  client: SupabaseClient,
  orderId: string,
): Promise<StageEvent[]> {
  const { data, error } = await client.rpc('supplier_stage_events', { p_order: orderId });
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    from_status: row.from_status,
    to_status: row.to_status,
    entered_at: row.entered_at,
    is_estimate: row.is_estimate,
  }));
}

/**
 * The proofs a factory should build from, newest version per component.
 *
 * order_assets keeps every version, so a component approved at v3 also has a
 * v1 and v2 sitting there approved-then-superseded. Showing all of them puts
 * three pictures of the same charm side by side and leaves somebody to work
 * out which is current — which is exactly the mistake this panel exists to
 * prevent.
 */
export function currentProofs(proofs: JobOrderProof[]): JobOrderProof[] {
  const best = new Map<string, JobOrderProof>();
  for (const proof of proofs) {
    const held = best.get(proof.component);
    if (held === undefined || proof.version > held.version) {
      best.set(proof.component, proof);
    }
  }
  return [...best.values()].sort((a, b) => a.component.localeCompare(b.component));
}

/**
 * What the factory is being asked for right now, in one sentence.
 *
 * Written from their side — this string only ever renders in the portal. The
 * shared stage model stays neutral precisely so this can be blunt.
 */
export function whatToDoNow(job: JobOrder): string {
  if (job.status === 'PO Sent to ES' && job.supplier_confirmed_at === null) {
    return 'Read the PO and the approved artwork below, then accept the order or ask TAP for a change.';
  }
  if (job.status === 'Sampling') {
    return job.sample_photo_count === 0
      ? 'Make the pre-production samples and upload a photo of each component.'
      : 'Samples uploaded. Add more component photos if any are still missing.';
  }
  if (job.status === 'Sample Approval') {
    return 'Your samples are with TAP. Nothing to do until they come back.';
  }
  if (job.status === 'In Production') {
    return job.supplier_ready_for_shipment
      ? 'Shipped. TAP will confirm delivery when it arrives.'
      : 'Build the full order, then mark it as shipped and add the tracking number.';
  }
  if (job.status === 'Shipped') {
    return 'On its way to the client.';
  }
  if (job.status === 'Delivered') {
    return 'Delivered. This job is complete.';
  }
  return 'With TAP.';
}
